import { createThemedUseStyletron, Theme } from 'baseui';
import { StyleObject } from "styletron-react";
import {
    COLOR_ACCENT,
    COLOR_ASPHALT,
    COLOR_DANGER,
    COLOR_GROUND_DARK,
    COLOR_GROUND_LIGHT,
    COLOR_SILVER,
    COLOR_TRANSLUCENT,
    COLOR_TRANSLUCENT_ALTERNATE, COLOR_TRANSLUCENT_STRONG, COLOR_TRANSLUCENT_STRONG_ALTERNATE,
    GLASS_BG_DARK, GLASS_BG_LIGHT, GLASS_BORDER_DARK, GLASS_BORDER_LIGHT, HAIRLINE_DARK, HAIRLINE_LIGHT
} from "../constants/colors";

// Radius scale — panels get LG, cards/rows MD, small controls SM.
export const RADIUS_SM = "8px";
export const RADIUS_MD = "12px";
export const RADIUS_LG = "16px";

// Soft layered shadows instead of single hard drops.
export const SHADOW_CARD = "0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.06)";
export const SHADOW_FLOAT = "0 2px 4px rgba(0, 0, 0, 0.05), 0 12px 32px rgba(0, 0, 0, 0.12)";
export const SHADOW_HOVER = "0 2px 4px rgba(0, 0, 0, 0.05), 0 8px 20px rgba(0, 0, 0, 0.10)";
export const SHADOW_DIALOG = "0 4px 10px rgba(0, 0, 0, 0.08), 0 24px 64px rgba(0, 0, 0, 0.24)";

/**
 * Opaque surfaces for content that sits ON the frosted panels.
 * `colors.backgroundPrimary` is deliberately `transparent` app-wide so panels can
 * float over the map — anything that needs to read as a raised card must set one
 * of these instead, or the map shows through and the card disappears.
 */
export const SURFACE_LIGHT = "#FFFFFF";
export const SURFACE_DARK = "#232928";

export const themedUseStyletron = createThemedUseStyletron<Theme & {
    customColors: {
        primary: string;
        accent: string;
        contentLight: string;
        translucent: string;
        translucentStrong: string;
        draggingOver: string;
        glass: string;
        glassBorder: string;
        hairline: string;
        /** Desk-mode page and settings ground */
        ground: string;
        /** opaque card on the ground */
        surface: string;
    }}>();

export const primitives = {
    warning: COLOR_DANGER,
};

const sharedBorders = {
    buttonBorderRadius: RADIUS_SM,
    inputBorderRadius: RADIUS_SM,
    checkboxBorderRadius: "4px",
    popoverBorderRadius: RADIUS_MD,
    surfaceBorderRadius: RADIUS_LG,
};

const sharedLighting = {
    shadow400: SHADOW_CARD,
    shadow500: SHADOW_CARD,
    shadow600: SHADOW_FLOAT,
    shadow700: SHADOW_FLOAT,
};

export const overridesLight = {
    colors: {
        backgroundPrimary: 'transparent',
        buttonSecondaryFill: "white",
        buttonSecondaryBorder: "black",
        inputFill: "white",
        inputBorder: "#55555533",
    },
    borders: sharedBorders,
    lighting: sharedLighting,
    customColors: {
        primary: COLOR_ACCENT + "AA",
        accent: COLOR_ACCENT,
        contentLight: COLOR_SILVER,
        translucent: COLOR_TRANSLUCENT,
        translucentStrong: COLOR_TRANSLUCENT_STRONG,
        draggingOver: "#ADDEC9",
        glass: GLASS_BG_LIGHT,
        glassBorder: GLASS_BORDER_LIGHT,
        hairline: HAIRLINE_LIGHT,
        ground: COLOR_GROUND_LIGHT,
        surface: SURFACE_LIGHT,
    }
};
export const overridesDark = {
    colors: {
        backgroundPrimary: 'transparent',
        buttonSecondaryFill: "black",
        inputFill: "black",
        inputBorder: "#FFFFFF33",
    },
    borders: sharedBorders,
    lighting: sharedLighting,
    customColors: {
        primary: COLOR_ACCENT + "AA",
        accent: COLOR_ACCENT,
        contentLight: COLOR_ASPHALT,
        translucent: COLOR_TRANSLUCENT_ALTERNATE,
        translucentStrong: COLOR_TRANSLUCENT_STRONG_ALTERNATE,
        draggingOver: "#10462D",
        glass: GLASS_BG_DARK,
        glassBorder: GLASS_BORDER_DARK,
        hairline: HAIRLINE_DARK,
        ground: COLOR_GROUND_DARK,
        surface: SURFACE_DARK,
    }
};

type GlassTheme = { colors: { contentPrimary: string }; customColors: { glass: string; glassBorder: string } };

// The heading of a section in a sheet (job, place, zone, member), so its parts are found at a glance.
export const sheetHeading = (theme: { colors: { contentPrimary: string } }): StyleObject => ({
    margin: 0,
    fontSize: "14px",
    lineHeight: "18px",
    fontWeight: 600,
    color: theme.colors.contentPrimary,
});

// The one frosted-panel recipe for surfaces floating over the map.
export const glassSurface = (theme: GlassTheme): StyleObject => ({
    backgroundColor: theme.customColors.glass,
    // text without a color of its own follows the theme; it would stay black on dark glass
    color: theme.colors.contentPrimary,
    backdropFilter: "blur(20px) saturate(1.4)",
    WebkitBackdropFilter: "blur(20px) saturate(1.4)",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: theme.customColors.glassBorder,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_FLOAT,
});

export const spreadSheetLightTheme = {
    "--jexcel_header_color": "#000",
    "--jexcel_header_color_highlighted": "#000",
    "--jexcel_header_background": "#f3f3f3",
    "--jexcel_header_background_highlighted": "#dcdcdc",
    "--jexcel_content_color": "#000",
    "--jexcel_content_color_highlighted": "#000",
    "--jexcel_content_background": "#fff",
    "--jexcel_content_background_highlighted": "rgba(0,0,0,0.05)",
    "--jexcel_menu_background": "#fff",
    "--jexcel_menu_background_highlighted": "#ebebeb",
    "--jexcel_menu_color": "#555",
    "--jexcel_menu_color_highlighted": "#555",
    "--jexcel_menu_box_shadow": "2px 2px 2px 0px rgba(143, 144, 145, 1)",
    "--jexcel_border_color": "#ccc",
    "--jexcel_border_color_highlighted": "#000",
    "--jexcel_active_color": "#007aff"
}

export const spreadSheetDarkTheme = {
    "--jexcel_header_color": "#fff",
    "--jexcel_header_color_highlighted": "#fff",
    "--jexcel_header_background": "#313131",
    "--jexcel_header_background_highlighted": "#777",
    "--jexcel_content_color": "#e9e9e9",
    "--jexcel_content_color_highlighted": "#fff",
    "--jexcel_content_background": "#3e3e3e",
    "--jexcel_content_background_highlighted": "#333",
    "--jexcel_menu_background": "#7e7e7e",
    "--jexcel_menu_background_highlighted": "#ebebeb",
    "--jexcel_menu_color": "#ddd",
    "--jexcel_menu_color_highlighted": "#222",
    "--jexcel_menu_box_shadow": "unset",
    "--jexcel_border_color": "#5f5f5f",
    "--jexcel_border_color_highlighted": "#999",
    "--jexcel_active_color": "#eee"
}
